// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React from 'react'
import PropTypes from 'prop-types'
import { Mutation } from 'react-apollo'
import moment from 'moment'

import {
  Input,
  Button,
  Heading,
  SelectWithData,
  EmployerGroupSelect,
  EmployerSelect,
  KeyValueTable as InputTable
} from '../../'

import {
  getDateOfBirthFromSouthAfricanID,
  normalizePhoneNumber,
  decodeBase64
} from '../../../utils'

import {
  MUTATION_IDENTITY,
  MUTATION_ADDRESS,
  MUTATION_CONTACT,
  MUTATION_USER,
  MUTATION_RESPONSIBILITY
} from '../../../mutations'


export class UserAdd extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    employerId: PropTypes.string.isRequired,
    onCancel: PropTypes.func,
    onSuccess: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  state = {
    identityExistsAlready: false,
    // identity info
    firstName: '',
    lastName: '',
    identityType: '',
    identityValue: '',
    // employer info
    employergroup: null,
    employer: null,
    // address info
    cellphoneNumber: null,
    emailAddress: null,
    // validation
    errorFields: []
  }

  validateInput = (field, value) => {
    if (field === 'identityValue') {
      return this.state.identityType === 'za_id'
        ? value.length === 13 && !isNaN(value)
        : value.length > 6
    }

    if (field === 'emailAddress') {
      return value && value.includes('@')  // very simple validation
    }

    return true
  }

  requiredInputProvided = () => (
    this.state.identityType &&
    this.state.identityValue &&
    this.state.firstName &&
    this.state.lastName &&
    this.state.emailAddress &&
    (this.state.employerGroup || this.state.employer)
  )

  canSave = () => this.requiredInputProvided() && this.state.errorFields.length === 0

  createIdentity = (identityMutate) => {
    let input = {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      identityType: this.state.identityType,
      identityValue: this.state.identityValue
    }

    if (this.state.dateOfBirth) {
      input.dateOfBirth = moment(this.state.dateOfBirth).format('YYYY-MM-DD')
    }

    return identityMutate({ variables: { input }})
  }

  createContact = (contactMutate, identityId) => {
    let input = {
      identity: identityId
    }

    if (this.state.employerGroup) {
      input.employergroup = this.state.employerGroup
    }
    if (this.state.employer) {
      input.employer = this.state.employer
    }

    contactMutate({ variables: { input }})
  }

  createAddress = (addressMutate, identityId, addressType, addressValue) => {
    addressMutate({
      variables: {
        input: {
          identity: identityId,
          addressType,
          addressValue,
          default: true
        }
      }
    })
  }

  setEmailAddressOnUser = (userMutate, userId) => {
    let input = {
      id: userId,
      email: this.state.emailAddress
    }
    userMutate({ variables: { input }})
  }

  addPermission = (responsibilityMutate, input) => (
    responsibilityMutate({ variables: { input }})
  )

  addPermissions = async (responsibilityMutate, identityId) => {
    let input = {
      identity: identityId,
      description: 'messageplus_user'
    }
    if (this.state.employer) {
      input.relatedModel = 'employer'
      await this.addPermission(responsibilityMutate, input)
    }
    if (this.state.employerGroup) {
      input.relatedModel = 'employergroup'
      input.relatedId = decodeBase64(this.state.employerGroup)
      await this.addPermission(responsibilityMutate, input)
    }
  }

  save = async (userMutate, responsibilityMutate, identityMutate, contactMutate, addressMutate) => {
    let result = await this.createIdentity(identityMutate)
    const identity = result.data.identityMutate.identity

    let mutationPromises = [
      this.createAddress(addressMutate, identity.id, 'email', this.state.emailAddress),
      this.createContact(contactMutate, identity.id),
      this.setEmailAddressOnUser(userMutate, identity.user.id),
      this.addPermissions(responsibilityMutate, identity.id)
    ]
    if (!!this.state.cellphoneNumber) {
      mutationPromises.push(
        this.createAddress(addressMutate, identity.id, 'msisdn', normalizePhoneNumber(this.state.cellphoneNumber))
      )
    }

    await Promise.all(mutationPromises)

    this.props.onSuccess()
  }

  autoFillAfterIdentityNumberInput = (e) => {
    let idNumber = e.target.value

    if (this.state.identityType === 'za_id') {
      let dob = getDateOfBirthFromSouthAfricanID(idNumber)

      this.setState({
        identityValue: idNumber,
        dateOfBirth: dob
      })
    }
  }

  handleIdentityTypeInputChange = (e) => {
    let idType = e.target.value

    if (idType === 'za_id'
      && this.state.identityValue !== ''
      && this.state.dateOfBirth === null) {

      let dob = getDateOfBirthFromSouthAfricanID(this.state.identityValue)

      this.setState({
        identityType: idType,
        dateOfBirth: dob
      })
    } else {
      this.setState({
        identityType: idType
      })
    }
  }

  handleInputValueChange = (e) => {
    let field = e.target.id
    let newValue = e.target.value

    let newState = {
      [field]: newValue
    }

    let index = this.state.errorFields.indexOf(field)
    if (this.validateInput(field, newValue) && index > -1) {
      // if new value validates and in errorFields state array, remove
      let errorFields = [...this.state.errorFields]
      errorFields.splice(index, 1)
      newState.errorFields = errorFields
    }
    else if (!this.validateInput(field, newValue) && index === -1) {
      // if new value does not validate and not in errorFields state array, add
      newState.errorFields = [...this.state.errorFields, field]
    }

    this.setState(newState)
  }

  getInputForm = () => {
    let requiredInputProvided = this.requiredInputProvided()
    return {
      '*Identity type': <SelectWithData
        id='identityType'
        forField='IdentityIdentityType'
        value={this.state.identityType}
        onChange={this.handleIdentityTypeInputChange}
        exclude={['cipc']}
      />,
      '*Identity number': <Input
        id='identityValue'
        placeholder='Identity number'
        value={this.state.identityValue || ''}
        onChange={this.handleInputValueChange}
        onBlur={this.autoFillAfterIdentityNumberInput}
        hasError={requiredInputProvided && this.state.errorFields.includes('identityValue')}
      />,
      '*First name': <Input
        id='firstName'
        placeholder='First name'
        value={this.state.firstName || ''}
        onChange={this.handleInputValueChange}
        hasError={requiredInputProvided && this.state.errorFields.includes('firstName')}
      />,
      '*Last name':  <Input
        id='lastName'
        placeholder='Last name'
        value={this.state.lastName || ''}
        onChange={this.handleInputValueChange}
        hasError={requiredInputProvided && this.state.errorFields.includes('lastName')}
      />,
      '*Email address': <Input
        id='emailAddress'
        placeholder='Enter address'
        value={this.state.emailAddress}
        onChange={this.handleInputValueChange}
        hasError={requiredInputProvided && this.state.errorFields.includes('emailAddress')}
      />,
      'Cellphone number': <Input
        id='cellphoneNumber'
        placeholder='Enter number'
        value={this.state.cellphoneNumber}
        onChange={this.handleInputValueChange}
      />,
      'Employer group': <EmployerGroupSelect
        id='employerGroup'
        placeholder='Choose employer group'
        value={this.state.employerGroup}
        onChange={this.handleInputValueChange}
      />,
      'Employer': <EmployerSelect
        id='employer'
        placeholder='Choose employer'
        value={this.state.employer}
        onChange={this.handleInputValueChange}
        employerGroupId={this.state.employerGroup}
      />
    }
  }

  render() {
    return (
      <div className={this.props.className}>
        {this.props.title &&
          <Heading className='slds-text-align_center'>
            {this.props.title}
          </Heading>
        }
        <InputTable
          data={this.getInputForm()}
          keyColumnSize='1-of-2'
        />
        <div className='slds-m-top_large slds-clearfix'>
          <div className='slds-text-align_center'>
            <Button
              onClick={this.props.onCancel}
              minWidth={120}
              className='u-text-caps'>
              Cancel
            </Button>
            <Mutation mutation={MUTATION_RESPONSIBILITY}>
              {(responsibilityMutate, { data }) => (
                <Mutation mutation={MUTATION_USER}>
                  {(userMutate, { data }) => (
                    <Mutation mutation={MUTATION_CONTACT}>
                      {(contactMutate, { data }) => (
                        <Mutation mutation={MUTATION_IDENTITY}>
                          {(identityMutate, { data }) => (
                            <Mutation mutation={MUTATION_ADDRESS}>
                              {(addressMutate, { data }) => (
                                <Button
                                  type='brand'
                                  onClick={() => this.save(userMutate, responsibilityMutate, identityMutate, contactMutate, addressMutate)}
                                  disabled={!this.canSave()}
                                  minWidth={120}
                                  className='u-text-caps'>
                                  Save
                                </Button>
                              )}
                            </Mutation>
                          )}
                        </Mutation>
                      )}
                    </Mutation>
                  )}
                </Mutation>
              )}
            </Mutation>
          </div>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/messageplus/user-add/index.js